/**
 * Per-section wrapper styles for the long, TOC-driven create/edit forms (product, owner).
 *
 * A gentle fade/slide-in when a section mounts (e.g. filtered back in by the field search), so
 * cards ease in instead of snapping. Plays once per mount — sections that stay visible across
 * keystrokes keep their DOM node and don't re-animate.
 *
 * ## Why it is shared rather than declared per page
 *
 * It was hand-rolled identically in `ProductEditPage` and `OwnerEditPage`, and `CreateOwnerPage`
 * had a third copy — and each page's loading SKELETON needs the same object again, which would have
 * made six. Per working-conventions §36 the second hand-roll is a shared module, and this is well
 * past that.
 *
 * The skeletons need it for PARITY, not tidiness: the animation's `from` state leaves a live
 * `translateY(6px)` transform on the settled page (measured — every section box on
 * /products/:id/edit reports `matrix(1, 0, 0, 1, 0, 6)`), so a skeleton card that is not wrapped in
 * the same `Collapse` + `Box sx={FORM_SECTION_SX}` sits 6px off the card it stands in for.
 *
 * It lives in `utils/` rather than being exported from one of the pages because each page imports
 * its own skeleton — a constant exported from a page and imported by that page's skeleton would be
 * a circular import, which in this repo resolves to `undefined` at module-init and fails silently
 * (see the NotFoundPage note in CertificatePage.tsx for the same trap).
 */
export const FORM_SECTION_SX = {
  width: '100%',
  scrollMarginTop: '24px',
  animation: 'fieldSectionIn 200ms ease',
  '@keyframes fieldSectionIn': {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}
